import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, User, Phone, Mail, Calendar, MapPin, Heart, Shield, 
  ChevronRight, ChevronLeft, Check, AlertCircle, Edit, Trash2, Eye,
  Users, UserPlus, RefreshCw, X, Download, FileText, Maximize2, Minimize2,
  Filter, FileSpreadsheet, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { downloadCSV, downloadExcel, EXPORT_COLUMNS } from '@/lib/exportUtils';
import SendMessageModal from '@/components/SendMessageModal';

const INITIAL_FORM_DATA = {
  // Step 1: Basic Info
  full_name: '',
  date_of_birth: '',
  gender: '',
  phone: '',
  // Step 2: Contact Details
  email: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  // Step 3: Emergency & Medical
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relation: '',
  blood_group: '',
  allergies: '',
  current_medications: '',
  medical_history: '',
  // Step 4: Insurance
  insurance_provider: '',
  insurance_policy_number: '',
  insurance_group_number: '',
  notes: ''
};

const STEPS = [
  { id: 1, title: 'Basic Information', icon: User, description: 'Name, DOB, Gender, Phone' },
  { id: 2, title: 'Contact Details', icon: MapPin, description: 'Email, Address' },
  { id: 3, title: 'Emergency & Medical', icon: Heart, description: 'Emergency contact, Medical info' },
  { id: 4, title: 'Insurance', icon: Shield, description: 'Insurance details (Optional)' }
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = ['Male', 'Female', 'Other'];

export default function Patients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  
  // Advanced filter states
  const [genderFilter, setGenderFilter] = useState('all');
  const [bloodGroupFilter, setBloodGroupFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [insuranceFilter, setInsuranceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Registration dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Edit/Delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // Send Message state
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);

  // Get unique values for filter dropdowns
  const uniqueCities = useMemo(() => {
    const cities = [...new Set(patients.map(p => p.city).filter(Boolean))];
    return cities.sort();
  }, [patients]);

  const uniqueInsuranceProviders = useMemo(() => {
    const providers = [...new Set(patients.map(p => p.insurance_provider).filter(Boolean))];
    return providers.sort();
  }, [patients]);

  // Advanced filtering
  const filteredPatients = useMemo(() => {
    let filtered = patients;
    
    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        patient.full_name?.toLowerCase().includes(query) ||
        patient.patient_number?.toLowerCase().includes(query) ||
        patient.phone?.includes(query) ||
        patient.email?.toLowerCase().includes(query)
      );
    }
    
    // Gender filter
    if (genderFilter && genderFilter !== 'all') {
      filtered = filtered.filter(patient => patient.gender === genderFilter);
    }
    
    // Blood group filter
    if (bloodGroupFilter && bloodGroupFilter !== 'all') {
      filtered = filtered.filter(patient => patient.blood_group === bloodGroupFilter);
    }
    
    // City filter
    if (cityFilter && cityFilter !== 'all') {
      filtered = filtered.filter(patient => patient.city === cityFilter);
    }
    
    // Insurance filter
    if (insuranceFilter && insuranceFilter !== 'all') {
      if (insuranceFilter === 'insured') {
        filtered = filtered.filter(patient => patient.insurance_provider);
      } else if (insuranceFilter === 'uninsured') {
        filtered = filtered.filter(patient => !patient.insurance_provider);
      } else {
        filtered = filtered.filter(patient => patient.insurance_provider === insuranceFilter);
      }
    }
    
    // Date range filter (by registration date)
    if (dateFrom) {
      filtered = filtered.filter(patient => {
        const patientDate = new Date(patient.created_at).toISOString().split('T')[0];
        return patientDate >= dateFrom;
      });
    }
    if (dateTo) {
      filtered = filtered.filter(patient => {
        const patientDate = new Date(patient.created_at).toISOString().split('T')[0];
        return patientDate <= dateTo;
      });
    }
    
    return filtered;
  }, [patients, searchQuery, genderFilter, bloodGroupFilter, cityFilter, insuranceFilter, dateFrom, dateTo]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setGenderFilter('all');
    setBloodGroupFilter('all');
    setCityFilter('all');
    setInsuranceFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchQuery || genderFilter !== 'all' || bloodGroupFilter !== 'all' || 
    cityFilter !== 'all' || insuranceFilter !== 'all' || dateFrom || dateTo;

  // Export handlers
  const handleExportCSV = () => {
    if (filteredPatients.length === 0) {
      toast.error('No data to export');
      return;
    }
    downloadCSV(filteredPatients, EXPORT_COLUMNS.patients, 'patients_export');
    toast.success(`Exported ${filteredPatients.length} patient(s) to CSV`);
  };

  const handleExportExcel = () => {
    if (filteredPatients.length === 0) {
      toast.error('No data to export');
      return;
    }
    downloadExcel(filteredPatients, EXPORT_COLUMNS.patients, 'patients_export');
    toast.success(`Exported ${filteredPatients.length} patient(s) to Excel`);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [patientsRes, statsRes] = await Promise.all([
        api.get('/patients'),
        api.get('/patients/stats')
      ]);
      setPatients(patientsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  };

  // Debounced duplicate check
  const checkDuplicate = useCallback(async (phone, email) => {
    if (!phone && !email) {
      setDuplicateWarning(null);
      return;
    }
    try {
      const params = new URLSearchParams();
      if (phone) params.append('phone', phone);
      if (email) params.append('email', email);
      const response = await api.get(`/patients/check-duplicate?${params}`);
      if (response.data.exists) {
        setDuplicateWarning(response.data.patient);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      // Silently fail duplicate check
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (dialogOpen && (formData.phone || formData.email)) {
        checkDuplicate(formData.phone, formData.email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.phone, formData.email, dialogOpen, checkDuplicate]);

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(1);
    setErrors({});
    setDuplicateWarning(null);
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
      if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
      if (!formData.gender) newErrors.gender = 'Gender is required';
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      else if (!/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone)) newErrors.phone = 'Enter a valid phone number';
    }
    
    if (step === 2) {
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    try {
      await api.post('/patients', formData);
      toast.success('Patient registered successfully!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to register patient'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickRegister = async () => {
    if (!validateStep(1)) return;
    
    setIsSubmitting(true);
    try {
      await api.post('/patients', formData);
      toast.success('Patient registered successfully!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to register patient'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPatient) return;
    
    setIsSubmitting(true);
    try {
      await api.put(`/patients/${selectedPatient.id}`, formData);
      toast.success('Patient updated successfully!');
      setEditDialogOpen(false);
      setSelectedPatient(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update patient'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPatient) return;
    
    try {
      await api.delete(`/patients/${selectedPatient.id}`);
      toast.success('Patient deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedPatient(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete patient');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/export/patients/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `patients_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Patients exported to ${format.toUpperCase()} successfully!`);
    } catch (error) {
      toast.error('Failed to export patients');
    }
  };

  const openEditDialog = (patient) => {
    setSelectedPatient(patient);
    setFormData({
      full_name: patient.full_name || '',
      date_of_birth: patient.date_of_birth || '',
      gender: patient.gender || '',
      phone: patient.phone || '',
      email: patient.email || '',
      address: patient.address || '',
      city: patient.city || '',
      state: patient.state || '',
      zip_code: patient.zip_code || '',
      emergency_contact_name: patient.emergency_contact_name || '',
      emergency_contact_phone: patient.emergency_contact_phone || '',
      emergency_contact_relation: patient.emergency_contact_relation || '',
      blood_group: patient.blood_group || '',
      allergies: patient.allergies || '',
      current_medications: patient.current_medications || '',
      medical_history: patient.medical_history || '',
      insurance_provider: patient.insurance_provider || '',
      insurance_policy_number: patient.insurance_policy_number || '',
      insurance_group_number: patient.insurance_group_number || '',
      notes: patient.notes || ''
    });
    setCurrentStep(1);
    setEditDialogOpen(true);
  };

  const openSendMessageModal = (patient) => {
    setMessageRecipient(patient);
    setSendMessageOpen(true);
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder="Enter patient's full name"
                className={errors.full_name ? 'border-rose-500' : ''}
                data-testid="patient-name-input"
              />
              {errors.full_name && <p className="text-xs text-rose-500">{errors.full_name}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth *</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                  max={new Date().toISOString().split('T')[0]}
                  className={errors.date_of_birth ? 'border-rose-500' : ''}
                  data-testid="patient-dob-input"
                />
                {formData.date_of_birth && (
                  <p className="text-xs text-slate-500">Age: {calculateAge(formData.date_of_birth)} years</p>
                )}
                {errors.date_of_birth && <p className="text-xs text-rose-500">{errors.date_of_birth}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">Gender *</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(value) => setFormData({...formData, gender: value})}
                >
                  <SelectTrigger className={errors.gender ? 'border-rose-500' : ''} data-testid="patient-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.gender && <p className="text-xs text-rose-500">{errors.gender}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="Enter phone number"
                className={errors.phone ? 'border-rose-500' : ''}
                data-testid="patient-phone-input"
              />
              {errors.phone && <p className="text-xs text-rose-500">{errors.phone}</p>}
            </div>

            {duplicateWarning && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>Potential duplicate found:</strong> {duplicateWarning.full_name} ({duplicateWarning.patient_number})
                  <Button 
                    variant="link" 
                    className="p-0 h-auto ml-2 text-amber-700"
                    onClick={() => {
                      setDialogOpen(false);
                      navigate(`/patients/${duplicateWarning.id}`);
                    }}
                  >
                    View patient →
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="patient@email.com"
                className={errors.email ? 'border-rose-500' : ''}
                data-testid="patient-email-input"
              />
              {errors.email && <p className="text-xs text-rose-500">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  placeholder="State"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  placeholder="12345"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Phone className="h-4 w-4" /> Emergency Contact
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                    placeholder="Contact name"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                    placeholder="Phone"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Relation</Label>
                  <Select 
                    value={formData.emergency_contact_relation} 
                    onValueChange={(value) => setFormData({...formData, emergency_contact_relation: value})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Relation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spouse">Spouse</SelectItem>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="child">Child</SelectItem>
                      <SelectItem value="sibling">Sibling</SelectItem>
                      <SelectItem value="friend">Friend</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="blood_group">Blood Group</Label>
                <Select 
                  value={formData.blood_group} 
                  onValueChange={(value) => setFormData({...formData, blood_group: value})}
                >
                  <SelectTrigger data-testid="patient-blood-group-select">
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map(bg => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="allergies">Allergies</Label>
                <Input
                  id="allergies"
                  value={formData.allergies}
                  onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                  placeholder="e.g., Penicillin, Peanuts"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="current_medications">Current Medications</Label>
              <Input
                id="current_medications"
                value={formData.current_medications}
                onChange={(e) => setFormData({...formData, current_medications: e.target.value})}
                placeholder="List any current medications"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="medical_history">Medical History</Label>
              <textarea
                id="medical_history"
                value={formData.medical_history}
                onChange={(e) => setFormData({...formData, medical_history: e.target.value})}
                className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                placeholder="Past surgeries, chronic conditions, etc."
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <Alert className="bg-purple-100 border-blue-200">
              <Shield className="h-4 w-4 text-purple-700" />
              <AlertDescription className="text-blue-700">
                Insurance information is optional. You can add this later.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="insurance_provider">Insurance Provider</Label>
              <Input
                id="insurance_provider"
                value={formData.insurance_provider}
                onChange={(e) => setFormData({...formData, insurance_provider: e.target.value})}
                placeholder="e.g., Blue Cross, Aetna"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="insurance_policy_number">Policy Number</Label>
                <Input
                  id="insurance_policy_number"
                  value={formData.insurance_policy_number}
                  onChange={(e) => setFormData({...formData, insurance_policy_number: e.target.value})}
                  placeholder="Policy #"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="insurance_group_number">Group Number</Label>
                <Input
                  id="insurance_group_number"
                  value={formData.insurance_group_number}
                  onChange={(e) => setFormData({...formData, insurance_group_number: e.target.value})}
                  placeholder="Group #"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                placeholder="Any additional notes about this patient"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="patients-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Patients
            </h1>
            <p className="text-slate-600 mt-2">Manage patient records and registration</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button 
              data-testid="add-patient-button" 
              className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Register Patient
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="border border-purple-200 bg-indigo-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{stats.total || 0}</p>
              <p className="text-xs text-purple-600">Total Patients</p>
            </CardContent>
          </Card>
          <Card className="border border-blue-200 bg-purple-100">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.male || 0}</p>
              <p className="text-xs text-purple-700">Male</p>
            </CardContent>
          </Card>
          <Card className="border border-pink-200 bg-pink-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-pink-700">{stats.female || 0}</p>
              <p className="text-xs text-pink-600">Female</p>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{stats.this_month || 0}</p>
              <p className="text-xs text-emerald-600">This Month</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6 border border-slate-200 bg-white">
          <CardContent className="p-4">
            {/* Search Box */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, patient number, phone, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-patients-input"
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 font-medium">Filters:</span>
              </div>

              {/* Gender Filter */}
              <Select value={genderFilter} onValueChange={setGenderFilter}>
                <SelectTrigger className="w-[120px]" data-testid="gender-filter">
                  <SelectValue placeholder="Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  {GENDERS.map((gender) => (
                    <SelectItem key={gender} value={gender}>{gender}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Blood Group Filter */}
              <Select value={bloodGroupFilter} onValueChange={setBloodGroupFilter}>
                <SelectTrigger className="w-[130px]" data-testid="blood-group-filter">
                  <SelectValue placeholder="Blood Group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Blood Groups</SelectItem>
                  {BLOOD_GROUPS.map((bg) => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* City Filter */}
              {uniqueCities.length > 0 && (
                <Select value={cityFilter} onValueChange={setCityFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="city-filter">
                    <SelectValue placeholder="City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {uniqueCities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Insurance Filter */}
              <Select value={insuranceFilter} onValueChange={setInsuranceFilter}>
                <SelectTrigger className="w-[150px]" data-testid="insurance-filter">
                  <SelectValue placeholder="Insurance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Patients</SelectItem>
                  <SelectItem value="insured">Insured</SelectItem>
                  <SelectItem value="uninsured">Uninsured</SelectItem>
                  {uniqueInsuranceProviders.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      {uniqueInsuranceProviders.map((provider) => (
                        <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>

              {/* Date From */}
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px]"
                  data-testid="date-from-filter"
                  title="Registered from"
                />
              </div>

              {/* Date To */}
              <div className="flex items-center gap-1">
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px]"
                  data-testid="date-to-filter"
                  title="Registered to"
                />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}

              {/* Export Buttons */}
              <div className="ml-auto flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <FileText className="mr-2 h-4 w-4" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Export as Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-3 text-sm text-slate-500">
              Showing {filteredPatients.length} of {patients.length} patient(s)
              {hasActiveFilters && ' (filtered)'}
            </div>
          </CardContent>
        </Card>

        {/* Patients Grid */}
        {filteredPatients.length === 0 ? (
          <Card className="border border-slate-200 bg-white">
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {patients.length === 0 ? 'No patients registered yet' : 'No patients match your filters'}
              </p>
              {patients.length === 0 && (
                <Button 
                  className="mt-4 bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                  onClick={() => {
                    resetForm();
                    setDialogOpen(true);
                  }}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Register First Patient
                </Button>
              )}
              {hasActiveFilters && patients.length > 0 && (
                <Button 
                  variant="outline"
                  className="mt-4"
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPatients.map((patient, index) => (
              <motion.div
                key={patient.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.03 }}
              >
                <Card 
                  className="border border-slate-200 shadow-sm hover:shadow-md transition-all bg-white group cursor-pointer"
                  onClick={() => navigate(`/patients/${patient.id}`)}
                  data-testid={`patient-card-${patient.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <User className="h-6 w-6 text-[#4A238F]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900 truncate">{patient.full_name}</h3>
                            <p className="text-sm text-purple-600 font-medium">{patient.patient_number}</p>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSendMessageModal(patient);
                              }}
                              title="Send Message"
                              data-testid={`send-message-btn-${patient.id}`}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/patients/${patient.id}`);
                              }}
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditDialog(patient);
                              }}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPatient(patient);
                                setDeleteDialogOpen(true);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-3 space-y-1.5">
                          {patient.date_of_birth && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Calendar className="h-3 w-3" />
                              {calculateAge(patient.date_of_birth)} years old
                              <Badge variant="outline" className="text-xs capitalize ml-1">
                                {patient.gender}
                              </Badge>
                            </div>
                          )}
                          {patient.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Phone className="h-3 w-3" />
                              {patient.phone}
                            </div>
                          )}
                          {patient.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 truncate">
                              <Mail className="h-3 w-3" />
                              {patient.email}
                            </div>
                          )}
                        </div>
                        {patient.blood_group && (
                          <Badge className="mt-2 bg-rose-100 text-rose-700 text-xs">
                            {patient.blood_group}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Registration Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setIsMaximized(false);
        }}>
          <DialogContent 
            className={`overflow-hidden flex flex-col transition-all duration-200 ${
              isMaximized 
                ? '!fixed !inset-2 !left-2 !top-2 !right-2 !bottom-2 !translate-x-0 !translate-y-0 !max-w-none !w-[calc(100vw-16px)] !h-[calc(100vh-16px)] !max-h-none' 
                : 'max-w-2xl max-h-[90vh]'
            }`}
          >
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-[#4A238F]" />
                  Register New Patient
                </DialogTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="h-8 w-8 p-0 hover:bg-slate-100"
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </div>
            </DialogHeader>
            
            {/* Step Indicator */}
            <div className="flex items-center justify-between px-2 py-4 border-b">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div 
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      currentStep === step.id 
                        ? 'bg-[#4A238F] text-white' 
                        : currentStep > step.id 
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
                  </div>
                  <div className="ml-2 hidden sm:block">
                    <p className={`text-sm font-medium ${currentStep === step.id ? 'text-slate-900' : 'text-slate-400'}`}>
                      {step.title}
                    </p>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-8 sm:w-16 h-0.5 mx-2 ${currentStep > step.id ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto px-1 py-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                {currentStep === 1 && (
                  <Button
                    variant="outline"
                    onClick={handleQuickRegister}
                    disabled={isSubmitting}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Quick Register
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                {currentStep > 1 && (
                  <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                )}
                {currentStep < 4 ? (
                  <Button onClick={handleNext} className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                    Next
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={isSubmitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="submit-patient-button"
                  >
                    {isSubmitting ? 'Registering...' : 'Complete Registration'}
                    <Check className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-[#4A238F]" />
                Edit Patient: {selectedPatient?.full_name}
              </DialogTitle>
            </DialogHeader>
            
            {/* Step Indicator for Edit */}
            <div className="flex items-center justify-center gap-2 py-2 border-b">
              {STEPS.map((step) => (
                <Button
                  key={step.id}
                  variant={currentStep === step.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentStep(step.id)}
                  className={currentStep === step.id ? 'bg-[#4A238F]' : ''}
                >
                  <step.icon className="h-4 w-4 mr-1" />
                  {step.title}
                </Button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto px-1 py-4">
              {renderStepContent()}
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpdate}
                disabled={isSubmitting}
                className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-600">
                <Trash2 className="h-5 w-5" />
                Delete Patient
              </DialogTitle>
            </DialogHeader>
            <p className="text-slate-600">
              Are you sure you want to delete <strong>{selectedPatient?.full_name}</strong>? 
              This action cannot be undone.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete Patient
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Message Modal */}
        <SendMessageModal
          open={sendMessageOpen}
          onClose={() => {
            setSendMessageOpen(false);
            setMessageRecipient(null);
          }}
          recipient={messageRecipient}
          recipientType="patient"
        />
      </motion.div>
    </div>
  );
}
